import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Default minimum image area (width * height in pixels) for the small-image
 * filter. Derived from the 90th-percentile analysis in the ingest handler —
 * images below this threshold are almost always logos or footer icons.
 */
export const DEFAULT_MIN_IMAGE_AREA = 62287;

/**
 * Filter Email Attachments Step
 *
 * Takes an inbound email, queries its associated CORE_DOCUMENT records,
 * and filters them by mime type, file size, and optional small-image
 * detection. Outputs only the documents that pass all configured filters.
 */
export const filterEmailAttachmentsStep = defineStepConfig({
  type: "filter_email_attachments",
  name: "Filter Email Attachments",
  description:
    "Filter an inbound email's attachments by file type, size, and image dimensions. Outputs only the documents that pass all filters.",
  category: "Transformation",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email whose attachments should be filtered",
    ),
  }),

  outputSchema: z.object({
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe("Attachments that passed all configured filters"),
    totalCount: z
      .number()
      .describe("Total number of attachments on the email before filtering"),
    filteredCount: z
      .number()
      .describe("Number of attachments that were removed by the filters"),
  }),

  configSchema: z
    .object({
      allowedMimeTypes: z
        .array(z.string())
        .optional()
        .describe(
          "Whitelist of mime type patterns. Supports wildcards (e.g. 'application/pdf', 'image/*'). If set, only attachments matching at least one pattern are kept. OR logic.",
        ),
      excludedMimeTypes: z
        .array(z.string())
        .optional()
        .describe(
          "Blacklist of mime type patterns. Attachments matching any pattern are removed. Applied after the whitelist. (e.g. 'image/png', 'image/gif')",
        ),
      minFileSizeBytes: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .describe(
          "Minimum file size in bytes. Attachments smaller than this are removed.",
        ),
      maxFileSizeBytes: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .describe(
          "Maximum file size in bytes. Attachments larger than this are removed.",
        ),
      skipSmallImages: z
        .boolean()
        .optional()
        .describe(
          "When enabled, image attachments are checked for pixel dimensions and removed if they are below the minImageArea threshold. Useful for filtering out icons, logos, and email footer images.",
        ),
      minImageArea: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .describe(
          `Minimum image area (width × height in pixels) for the small-image filter. Only used when skipSmallImages is enabled. Defaults to ${DEFAULT_MIN_IMAGE_AREA}.`,
        ),
    })
    .refine(
      (c) =>
        c.minFileSizeBytes == null ||
        c.maxFileSizeBytes == null ||
        c.minFileSizeBytes <= c.maxFileSizeBytes,
      {
        message: "minFileSizeBytes must be ≤ maxFileSizeBytes",
        path: ["minFileSizeBytes"],
      },
    ),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "2 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
